import { Link } from 'react-router-dom';
import { Eye, Check, X, Ban } from 'lucide-react';
import { Badge } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { EmptyState } from '@/components/ui/EmptyState';
import type { Commission, CommissionStatus } from '../types/admin-partner';

interface CommissionTableProps {
  commissions: Commission[];
  isLoading: boolean;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onApprove: (id: string) => void;
  onCancel: (id: string) => void;
  onBlock: (id: string) => void;
  isApproving?: boolean;
  isCancelling?: boolean;
  isBlocking?: boolean;
  selectedIds?: Set<string>;
  onSelectId?: (id: string) => void;
  onSelectAll?: () => void;
}

const statusVariants: Record<CommissionStatus, 'warning' | 'primary' | 'success' | 'outline' | 'danger'> = {
  PENDING: 'warning',
  APPROVED: 'primary',
  AVAILABLE: 'success',
  PAID: 'outline',
  CANCELLED: 'danger',
  BLOCKED: 'danger',
};

const statusLabels: Record<CommissionStatus, string> = {
  PENDING: 'Pendente',
  APPROVED: 'Aprovada',
  AVAILABLE: 'Disponível',
  PAID: 'Paga',
  CANCELLED: 'Cancelada',
  BLOCKED: 'Bloqueada',
};

export function CommissionTable({
  commissions,
  isLoading,
  total,
  page,
  pageSize,
  onPageChange,
  onApprove,
  onCancel,
  onBlock,
  isApproving,
  isCancelling,
  isBlocking,
  selectedIds,
  onSelectId,
  onSelectAll,
}: CommissionTableProps) {
  const formatDate = (dateStr: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(new Date(dateStr));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(amount);
  };

  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(1)}%`;
  };

  if (isLoading) {
    return <LoadingOverlay message="Carregando comissões..." />;
  }

  if (commissions.length === 0) {
    return (
      <EmptyState
        title="Nenhuma comissão encontrada"
        description="Não há comissões cadastradas com os filtros selecionados."
      />
    );
  }

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {onSelectId && (
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={selectedIds?.size === commissions.length && commissions.length > 0}
                    onChange={onSelectAll}
                    className="rounded border-input"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Parceiro</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Paciente</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Valor</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Taxa</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Campanha</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Status</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Data</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {commissions.map((commission) => (
              <tr key={commission.id} className="hover:bg-muted/30">
                {onSelectId && (
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds?.has(commission.id)}
                      onChange={() => onSelectId(commission.id)}
                      className="rounded border-input"
                    />
                  </td>
                )}
                <td className="px-4 py-3 text-sm font-medium">{commission.partnerName}</td>
                <td className="px-4 py-3 text-sm">{commission.patientName}</td>
                <td className="px-4 py-3 text-sm font-medium">
                  {formatCurrency(commission.amount)}
                </td>
                <td className="px-4 py-3 text-sm">{formatRate(commission.rate)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {commission.campaignName || '-'}
                </td>
                <td className="px-4 py-3">
                  <Badge variant={statusVariants[commission.status]}>
                    {statusLabels[commission.status]}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {formatDate(commission.createdAt)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <Link to={`/admin/partners/${commission.partnerId}`}>
                      <Button variant="ghost" size="sm" title="Ver Parceiro">
                        <Eye className="h-4 w-4" />
                      </Button>
                    </Link>
                    {commission.status === 'PENDING' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onApprove(commission.id)}
                        disabled={isApproving}
                        title="Aprovar"
                        className="text-success hover:text-success"
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    {(commission.status === 'PENDING' || commission.status === 'APPROVED') && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onCancel(commission.id)}
                        disabled={isCancelling}
                        title="Cancelar"
                        className="text-destructive hover:text-destructive"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                    {commission.status !== 'BLOCKED' && commission.status !== 'CANCELLED' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onBlock(commission.id)}
                        disabled={isBlocking}
                        title="Bloquear"
                        className="text-warning hover:text-warning"
                      >
                        <Ban className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between">
        <PaginationInfo
          currentPage={page}
          pageSize={pageSize}
          totalItems={total}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={onPageChange}
        />
      </div>
    </div>
  );
}
